/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Heartbeat, Certificate, QrCode, ShieldCheck, Book, ChartPie,
  Syringe, ArrowRight, ClipboardText, GraduationCap, Hospital, FirstAid,
  Microscope, Bandaids, FileText, Siren,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

export default function ForHealthcare() {
  useEffect(() => {
    document.title = "Biomedical Waste Compliance for Modern Healthcare · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="page-for-healthcare">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-b from-[#F0F5EE] to-white">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 70% 40%, rgba(181,45,45,0.06) 0%, transparent 45%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow color="#B12A1E">Healthcare · BMW Compliance</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 leading-[1.05] text-slate-900">
              Biomedical waste compliance, built for <span className="text-[#B12A1E] italic">modern healthcare</span>.
            </h1>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed max-w-xl">
              From bedside segregation to state-board filings — every touchpoint
              instrumented. BMW Rules 2016, NABH, JCI — your inspections start
              pre-aligned.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/#contact" data-testid="hc-cta-demo" className={BTN_GREEN}>
                Schedule compliance demo <ArrowRight size={14} weight="bold"/>
              </Link>
              <Link to="/training" data-testid="hc-cta-training"
                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 text-sm font-medium">
                Staff certification program
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 text-[11px] uppercase tracking-[0.2em] text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck size={13} weight="fill" className="text-[#B12A1E]"/>BMW Rules 2016</span>
              <span>·</span><span>NABH ready</span><span>·</span><span>CPCB authorised</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
                       className="relative">
            <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Chain of custody · Live</div>
                  <div className="font-heading text-lg text-slate-900">QR Bag RSG-BAG-98234</div>
                </div>
                <div className="text-[10px] px-2 py-1 bg-[#D4E0D1] text-[#1A3317] rounded font-semibold uppercase tracking-[0.15em]">Authorized</div>
              </div>
              <div className="space-y-3">
                <CustodyStep c="#FFC801" t="Generated · Ward 3B" time="08:42"/>
                <CustodyStep c="#FF8A00" t="Segregated · Yellow" time="08:44"/>
                <CustodyStep c="#E11D48" t="Collected · Rescigo Field Staff" time="09:15"/>
                <CustodyStep c="#0A2540" t="In transit · RSG-TRK-014" time="09:22"/>
                <CustodyStep c="#2D5A27" t="Disposed · CBWTF authorized" time="11:30" final/>
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 text-[10px] text-slate-500 flex items-center justify-between">
                <span>Waste type · Yellow category (infectious)</span>
                <span className="font-mono">BMW-YEL-98234</span>
              </div>
            </div>
            <div className="absolute -top-5 -right-5 w-28 h-28 rounded-full bg-[#B12A1E]/10 blur-2xl"/>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow color="#B12A1E">Capabilities</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Clinical-grade biomedical infrastructure.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <FeatureCard accent="#B12A1E" Icon={ClipboardText} title="BMW Rule Compliance" desc="Colour-coded segregation tracking, manifest generation, authorized transport — auto-aligned to BMW Rules 2016."/>
            <FeatureCard accent="#B12A1E" Icon={QrCode} title="QR Chain-of-Custody" desc="Every bag tagged at source. Scan-verifiable at every touchpoint — ward, transit, disposal."/>
            <FeatureCard accent="#2D5A27" Icon={GraduationCap} title="Staff Certification LMS" desc="Role-specific curricula (doctor / nurse / housekeeping / admin). QR-verifiable certificates in the Rescigo Academy."/>
            <FeatureCard accent="#2D5A27" Icon={Book} title="Audit-ready Trails" desc="Every action time-stamped, signed, and exportable. NABH, JCI, pollution board audits become paperwork-free."/>
            <FeatureCard accent="#B12A1E" Icon={Hospital} title="Hospital Dashboards" desc="Ward-wise segregation compliance, per-department waste generation, occupational safety KPIs."/>
            <FeatureCard accent="#B12A1E" Icon={FileText} title="NABH Readiness" desc="BMW chapter readiness tracker — pre-filled evidence, gap analysis, and remediation workflow."/>
          </div>
        </div>
      </section>

      {/* TARGET INSTITUTIONS */}
      <section className="py-24 bg-[#F7F6F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow color="#B12A1E">Institutions we serve</Eyebrow>
          <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">From multi-speciality to single-chair clinics.</h2>
          <div className="grid md:grid-cols-4 gap-5 mt-12">
            <Inst Icon={Hospital} title="Hospitals" desc="200+ bed facilities, multi-speciality, teaching hospitals, super-speciality centres."/>
            <Inst Icon={FirstAid} title="Clinics & Nursing Homes" desc="Solo & group practices, day-care clinics, nursing homes, fertility centres."/>
            <Inst Icon={Microscope} title="Diagnostic Labs" desc="Pathology chains, imaging centres, genomics labs, IVF labs."/>
            <Inst Icon={Bandaids} title="Specialty Clinics" desc="Dental, dermatology, dialysis, ophthalmology, ortho & rehab centres."/>
          </div>
        </div>
      </section>

      {/* CERTIFICATION FLOW */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow color="#2D5A27">Training & Certification</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Every staff member, inspection-ready.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Our Rescigo Academy delivers role-specific BMW training
              across your institution. Co-branded certificates, QR-verifiable,
              valid for 1 year — re-certification reminders automated.
            </p>
            <div className="mt-6"><BulletList color="#2D5A27" items={[
              "4 role-based tracks: Doctor · Nurse · Housekeeping · Admin",
              "Video modules + 10-question bank per course",
              "100% pass threshold with random 5-question draw",
              "Premium co-branded PDF with QR auth-check",
              "Bulk CSV upload for staff onboarding",
              "Institutional dashboard with per-department completion rates",
            ]}/></div>
            <Link to="/training" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2D5A27]">
              Explore the Academy <ArrowRight size={13} weight="bold"/>
            </Link>
          </div>
          <div className="bg-gradient-to-br from-[#B12A1E] to-[#7A1D15] text-white rounded-2xl p-10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, white 0%, transparent 50%)" }}/>
            <div className="relative">
              <Certificate size={42} weight="duotone" className="opacity-80"/>
              <div className="font-heading text-3xl mt-5 leading-tight max-w-md">
                "Rescigo cut our BMW audit prep from 3 weeks to 2 days."
              </div>
              <div className="mt-5 text-sm text-white/70">
                — Dr. Priya Nair · Compliance Officer, Amrita Institute of Medical Sciences
              </div>
              <div className="mt-8 pt-6 border-t border-white/20 grid grid-cols-3 gap-4 text-xs">
                <div><div className="font-heading text-2xl">1,280</div><div className="text-white/60">Staff certified</div></div>
                <div><div className="font-heading text-2xl">14</div><div className="text-white/60">Departments live</div></div>
                <div><div className="font-heading text-2xl">100%</div><div className="text-white/60">NABH BMW chapter</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Healthcare Compliance"
        title="Inspection-ready, every day of the year."
        body="Our healthcare team walks you through a gap-analysis on your existing BMW programme and a 30-day deployment plan — at zero upfront cost."
        primary={{ label: "Schedule compliance demo", to: "/#contact", testid: "hc-cta-bottom" }}
        secondary={{ label: "Training Academy", to: "/training" }}
      />

      <MarketingFooter/>
    </div>
  );
}

const CustodyStep = ({ c, t, time, final }) => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <div className="w-3 h-3 rounded-full" style={{ background: c, boxShadow: `0 0 0 4px ${c}20` }}/>
      {!final && <div className="absolute top-3 left-1/2 -translate-x-1/2 w-px h-3 bg-slate-200"/>}
    </div>
    <div className="flex-1 flex items-center justify-between text-sm">
      <div className="font-medium text-slate-800">{t}</div>
      <div className="text-[10px] text-slate-500 font-mono">{time}</div>
    </div>
  </div>
);

const Inst = ({ Icon, title, desc }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-white rounded-2xl ring-1 ring-slate-200 p-7 hover:shadow-lg transition">
    <Icon size={28} weight="duotone" className="text-[#B12A1E]"/>
    <h3 className="font-heading text-lg text-slate-900 mt-4">{title}</h3>
    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
  </motion.div>
);
